import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  StickyNote,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Clock,
  User,
  Tag,
  Search
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card'
import { Button } from '../ui/button'

const AdminNotes = ({ clientId }) => {
  const [isCreating, setIsCreating] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [newNote, setNewNote] = useState({ title: '', content: '', tags: [] })
  const [tagInput, setTagInput] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [noteToDelete, setNoteToDelete] = useState(null)
  const queryClient = useQueryClient()

  // Fetch client notes
  const { data: notes = [], isLoading, error } = useQuery(
    ['admin-notes', clientId],
    () => apiService.get(`/admin-notes/client/${clientId}`).then(res => res.data.notes),
    { 
      enabled: !!clientId,
      refetchOnWindowFocus: false 
    }
  )

  // Create note mutation
  const createNoteMutation = useMutation(
    (noteData) => apiService.post(`/admin-notes/client/${clientId}`, noteData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-notes', clientId])
        setIsCreating(false)
        setNewNote({ title: '', content: '', tags: [] })
        setTagInput('')
        toast.success('Note created successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create note')
      }
    }
  )

  // Update note mutation
  const updateNoteMutation = useMutation(
    ({ noteId, ...noteData }) => apiService.put(`/admin-notes/${noteId}`, noteData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-notes', clientId])
        setEditingNote(null)
        toast.success('Note updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update note')
      }
    }
  )

  // Delete note mutation
  const deleteNoteMutation = useMutation(
    (noteId) => apiService.delete(`/admin-notes/${noteId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-notes', clientId])
        toast.success('Note deleted successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete note')
      }
    }
  )

  const handleCreateNote = () => {
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toast.error('Please fill in both title and content')
      return
    }

    createNoteMutation.mutate(newNote)
  }

  const handleUpdateNote = () => {
    if (!editingNote.title.trim() || !editingNote.content.trim()) {
      toast.error('Please fill in both title and content')
      return
    }

    updateNoteMutation.mutate(editingNote)
  }

  const handleDeleteNote = (note) => {
    setNoteToDelete(note)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (noteToDelete) {
      deleteNoteMutation.mutate(noteToDelete.id)
      setShowDeleteConfirm(false)
      setNoteToDelete(null)
    }
  }

  const addTag = (tags, setTags, input, setInput) => {
    const tag = input.trim().toLowerCase()
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag])
      setInput('')
    }
  }

  const removeTag = (tags, setTags, tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <div className="p-2 bg-warning-100 dark:bg-warning-900/20 rounded-lg">
              <StickyNote className="h-5 w-5 text-warning-600 dark:text-warning-400" />
            </div>
            <span>Admin Notes</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" text="Loading notes..." />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-3">
            <div className="p-2 bg-warning-100 dark:bg-warning-900/20 rounded-lg">
              <StickyNote className="h-5 w-5 text-warning-600 dark:text-warning-400" />
            </div>
            <span>Admin Notes</span>
            <span className="ml-2 text-sm font-normal text-neutral-500 dark:text-neutral-400">
              ({notes.length})
            </span>
          </CardTitle>
          <Button
            onClick={() => setIsCreating(true)}
            size="sm"
            disabled={isCreating}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Note
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {/* Create New Note Form */}
        {isCreating && (
          <div className="mb-6 p-4 border border-neutral-200 dark:border-[#3a3548] rounded-lg bg-neutral-50 dark:bg-[#2a2538]">
            <h4 className="text-md font-medium mb-4 text-neutral-900 dark:text-neutral-100">Create New Note</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={newNote.title}
                  onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                  className="h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  placeholder="Note title..."
                  maxLength={255}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Content
                </label>
                <textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
                  placeholder="Note content..."
                  maxLength={10000}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newNote.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-200"
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                      <button
                        onClick={() => removeTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tag)}
                        className="ml-1 text-accent-600 hover:text-accent-800 dark:text-accent-400 dark:hover:text-accent-300"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex border border-neutral-200 dark:border-[#3a3548] rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-accent-500 focus-within:border-transparent transition-all duration-200">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value.includes(',')) {
                        const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag)
                        tags.forEach(tag => {
                          addTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tag, () => {})
                        })
                        setTagInput('')
                      } else {
                        setTagInput(value)
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tagInput, setTagInput)
                      }
                    }}
                    className="h-9 flex-1 px-3 bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none border-0"
                    placeholder="Add tag..."
                    maxLength={50}
                  />
                  <button
                    type="button"
                    onClick={() => addTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tagInput, setTagInput)}
                    className="px-3 py-2 bg-neutral-50 dark:bg-[#2a2538] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-[#3a3548] border-l border-neutral-200 dark:border-[#3a3548] transition-colors duration-200"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false)
                    setNewNote({ title: '', content: '', tags: [] })
                    setTagInput('')
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreateNote}
                  disabled={createNoteMutation.isLoading}
                >
                  {createNoteMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-1" />
                      Save Note
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        <div className="space-y-4">
          {notes.length > 0 ? (
            notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                isEditing={editingNote?.id === note.id}
                editingNote={editingNote}
                setEditingNote={setEditingNote}
                onUpdate={handleUpdateNote}
                onDelete={handleDeleteNote}
                updateMutation={updateNoteMutation}
                formatDate={formatDate}
                addTag={addTag}
                removeTag={removeTag}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <StickyNote className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                No admin notes yet
              </h4>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                Create your first note to keep track of important client information.
              </p>
              {!isCreating && (
                <Button
                  size="sm"
                  onClick={() => setIsCreating(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Note
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && noteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-content bg-white dark:bg-[#221f2f] rounded-lg p-6 max-w-md w-full mx-4 border border-neutral-200 dark:border-[#2f2b41]">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
              Delete Note
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Are you sure you want to delete the note <strong>"{noteToDelete.title}"</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setNoteToDelete(null)
                }}
                className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteNoteMutation.isLoading}
                className="bg-error-600 hover:bg-error-700 text-white"
              >
                {deleteNoteMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

// Individual Note Card Component
const NoteCard = ({ 
  note, 
  isEditing, 
  editingNote, 
  setEditingNote, 
  onUpdate, 
  onDelete, 
  updateMutation,
  formatDate,
  addTag,
  removeTag
}) => {
  const [tagInput, setTagInput] = useState('')

  const startEditing = () => {
    setEditingNote({ ...note })
  }

  const cancelEditing = () => {
    setEditingNote(null)
    setTagInput('')
  }

  if (isEditing) {
    return (
      <div className="p-4 border border-neutral-200 dark:border-[#3a3548] rounded-lg bg-neutral-50 dark:bg-[#2a2538]">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={editingNote.title}
              onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
              className="h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              maxLength={255}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Content
            </label>
            <textarea
              value={editingNote.content}
              onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
              maxLength={10000}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {editingNote.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-200"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                  <button
                    onClick={() => removeTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tag)}
                    className="ml-1 text-accent-600 hover:text-accent-800 dark:text-accent-400 dark:hover:text-accent-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex border border-neutral-200 dark:border-[#3a3548] rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-accent-500 focus-within:border-transparent transition-all duration-200">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.includes(',')) {
                    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag)
                    tags.forEach(tag => {
                      addTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tag, () => {})
                    })
                    setTagInput('')
                  } else {
                    setTagInput(value)
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tagInput, setTagInput)
                  }
                }}
                className="h-9 flex-1 px-3 bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none border-0"
                placeholder="Add tag..."
                maxLength={50}
              />
              <button
                type="button"
                onClick={() => addTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tagInput, setTagInput)}
                className="px-3 py-2 bg-neutral-50 dark:bg-[#2a2538] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-[#3a3548] border-l border-neutral-200 dark:border-[#3a3548] transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={cancelEditing}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onUpdate}
              disabled={updateMutation.isLoading}
            >
              {updateMutation.isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 border border-neutral-200 dark:border-[#3a3548] rounded-lg hover:bg-neutral-50 dark:hover:bg-[#2a2538] transition-colors duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h5 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">
            {note.title}
          </h5>
          
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 whitespace-pre-wrap">
            {note.content}
          </p>

          {note.tags && note.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {note.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-300"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center text-xs text-neutral-500 space-x-4">
            <div className="flex items-center">
              <User className="h-3 w-3 mr-1" />
              {note.author_name}
            </div>
            <div className="flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatDate(note.created_at)}
            </div>
            {note.updated_at !== note.created_at && (
              <div className="text-neutral-400">
                (edited {formatDate(note.updated_at)})
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-1 ml-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={startEditing}
            className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            title="Edit note"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(note)}
            className="p-1 text-neutral-400 hover:text-error-600"
            title="Delete note"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AdminNotes